import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '../context/WalletContext';

function PortfolioTracker() {
    const { balances, prices, solTokens } = useWallet();
    const hiddenSpl    = JSON.parse( localStorage.getItem( 'chainvault_hidden_spl' ) || '[]' );
    const customTokens = JSON.parse( localStorage.getItem( 'chainvault_custom_tokens' ) || '[]' );
    const assets = [
        { name: 'Solana',   symbol: 'SOL', balance: parseFloat(balances.solana   || 0), price: prices?.solana?.usd      || 0, icon: 'https://assets.coingecko.com/coins/images/4128/small/solana.png',   color: '#9945FF' },
        { name: 'Ethereum', symbol: 'ETH', balance: parseFloat(balances.ethereum || 0), price: prices?.ethereum?.usd    || 0, icon: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png',  color: '#627EEA' },
        { name: 'BNB',      symbol: 'BNB', balance: parseFloat(balances.bsc      || 0), price: prices?.binancecoin?.usd || 0, icon: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png', color: '#F0B90B' },
        ...(solTokens || []).filter(t => t.symbol !== 'SOL' && !hiddenSpl.includes(t.mint)).map(t => { const custom = customTokens.find(c => c.address === t.mint); return { name: custom ? custom.name : t.name, symbol: custom ? custom.symbol : t.symbol, balance: parseFloat(t.balance || 0), price: t.symbol === 'USDT' ? 1 : t.symbol === 'USDC' ? 1 : 0, icon: t.icon, color: t.color }; }),
    ].filter(a => a.balance > 0);
    const totalUSD = assets.reduce((sum, a) => sum + (a.balance * a.price), 0);
    return (
        <div className="cv-app-content">
            <div className="cv-portfolio-total">
                <span className="cv-portfolio-label">Total Portfolio Value</span>
                <span className="cv-portfolio-value">${totalUSD.toFixed(2)} USD</span>
            </div>
            <div className="cv-portfolio-list">
                {assets.length === 0 ? (
                    <div className="cv-app-empty"><p>Connect your wallet to see your portfolio</p></div>
                ) : assets.map((asset, i) => (
                    <div key={i} className="cv-portfolio-row">
                        <div className="cv-portfolio-icon-wrap">
                            {asset.icon ? <img src={asset.icon} alt={asset.name} className="cv-asset-icon" /> : <div className="cv-asset-icon-placeholder-filled" style={{background: asset.color}}>{asset.symbol.charAt(0)}</div>}
                        </div>
                        <div className="cv-portfolio-info">
                            <span className="cv-portfolio-name">{asset.name}</span>
                            <span className="cv-portfolio-balance">{asset.balance.toFixed(6)} {asset.symbol}</span>
                        </div>
                        <div className="cv-portfolio-usd">
                            <span>${(asset.balance * asset.price).toFixed(2)}</span>
                            {asset.price > 0 && <span className="cv-portfolio-price">@ ${asset.price.toLocaleString()}</span>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function NFTGallery() {
    const { address, walletType, builtInWallet } = useWallet();
    const [nfts,        setNfts       ] = useState([]);
    const [loading,     setLoading    ] = useState(false);
    const [error,       setError      ] = useState(null);
    const [selectedNft, setSelectedNft] = useState(null);

    const ethAddress = (walletType === 'builtin' && builtInWallet)
        ? builtInWallet.ethereum.address
        : address;

    const fetchNFTs = useCallback(async () => {
        if (!ethAddress) return;
        setLoading(true); setError(null); setNfts([]);
        try {
            const res  = await fetch('https://api.opensea.io/api/v2/chain/ethereum/account/' + ethAddress + '/nfts?limit=20', { headers: { accept: 'application/json' } });
            const data = await res.json();
            if (data && data.results && data.results.length > 0) {
                setNfts(data.results.map(n => ({
                    id:         n.identifier,
                    name:       n.name || 'Unnamed NFT #' + n.identifier,
                    image:      n.image_url || n.display_image_url,
                    collection: n.collection,
                    floor:      null,
                    currency:   'ETH',
                    link:       'https://opensea.io/assets/ethereum/' + n.contract + '/' + n.identifier,
                })));
            } else {
                setError('No Ethereum NFTs found for this wallet.');
            }
        } catch (err) { setError('Failed to load NFTs. Please try again.'); }
        finally { setLoading(false); }
    }, [ethAddress]);

    useEffect(() => { if (ethAddress) fetchNFTs(); }, [fetchNFTs, ethAddress]);

    if (!ethAddress) return (
        <div className="cv-app-content">
            <div className="cv-app-empty">
                <div className="cv-app-empty-icon">🖼️</div>
                <p>NFT Gallery</p>
                <span>Connect your wallet to view your Ethereum NFT collection.</span>
            </div>
        </div>
    );

    return (
        <div className="cv-app-content">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px',flexWrap:'wrap',gap:'10px'}}>
                <div>
                    <span style={{fontSize:'13px',color:'#888'}}>Showing NFTs for: </span>
                    <span style={{fontSize:'13px',color:'#6C63FF',fontWeight:'600'}}>{ethAddress.slice(0,8)}...{ethAddress.slice(-4)}</span>
                    <span style={{fontSize:'11px',color:'#888',marginLeft:'8px',background:'rgba(108,99,255,0.1)',padding:'2px 8px',borderRadius:'20px'}}>⟠ Ethereum</span>
                </div>
                <button className="cv-btn-secondary" onClick={fetchNFTs} disabled={loading}>
                    {loading ? 'Loading...' : '⟳ Refresh'}
                </button>
            </div>

            {loading && <div className="cv-market-loading"><div className="cv-market-spinner"></div><p>Loading your NFTs...</p></div>}

            {error && !loading && (
                <div className="cv-app-empty">
                    <div className="cv-app-empty-icon">🖼️</div>
                    <p>{error}</p>
                    <span style={{fontSize:'12px',color:'#888',marginTop:'8px',display:'block'}}>
                        Solana NFT support coming in next version.
                    </span>
                </div>
            )}

            {!loading && nfts.length > 0 && (
                <div className="cv-nft-grid">
                    {nfts.map((nft, i) => (
                        <div key={i} className="cv-nft-card" onClick={() => setSelectedNft(nft)}>
                            {nft.image
                                ? <img src={nft.image} alt={nft.name} className="cv-nft-image" onError={e => { e.target.style.display='none'; }} />
                                : <div className="cv-nft-image-placeholder">🖼️</div>
                            }
                            <div className="cv-nft-info">
                                <span className="cv-nft-name">{nft.name}</span>
                                <span className="cv-nft-collection">{nft.collection}</span>
                                {nft.floor && <span className="cv-nft-floor">Floor: {nft.floor} {nft.currency}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {selectedNft && (
                <div className="cv-modal-overlay" onClick={() => setSelectedNft(null)}>
                    <div className="cv-modal" onClick={e => e.stopPropagation()} style={{maxWidth:'480px',width:'90%'}}>
                        <div className="cv-modal-header">
                            <h3>{selectedNft.name}</h3>
                            <button className="cv-modal-close" onClick={() => setSelectedNft(null)}>✕</button>
                        </div>
                        <div style={{padding:'20px'}}>
                            {selectedNft.image && <img src={selectedNft.image} alt={selectedNft.name} style={{width:'100%',borderRadius:'12px',marginBottom:'16px'}} />}
                            <div style={{display:'flex',flexDirection:'column',gap:'8px',marginBottom:'16px'}}>
                                <div style={{display:'flex',justifyContent:'space-between'}}>
                                    <span style={{color:'#888',fontSize:'13px'}}>Collection</span>
                                    <span style={{fontWeight:'600'}}>{selectedNft.collection}</span>
                                </div>
                            </div>
                            <a href={selectedNft.link} target="_blank" rel="noopener noreferrer" className="cv-btn-primary full-width" style={{display:'block',textAlign:'center',textDecoration:'none',padding:'12px',borderRadius:'8px'}}>
                                View on OpenSea ↗
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function IDOLaunchpad() {
    const [projects, setProjects] = useState([]);
    const [loading,  setLoading ] = useState(true);

    const formatLarge = num => {
        if (!num) return '-';
        if (num >= 1e9) return '$' + (num/1e9).toFixed(2) + 'B';
        if (num >= 1e6) return '$' + (num/1e6).toFixed(2) + 'M';
        return '$' + num.toLocaleString();
    };

    useEffect(() => {
        fetch('https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=volume_desc&per_page=10&page=1&sparkline=false&price_change_percentage=24h')
        .then(r => r.json()).then(data => {
            if (Array.isArray(data)) setProjects(data.map(c => ({ name: c.name, symbol: c.symbol.toUpperCase(), price: '$' + (c.current_price||0).toFixed(4), change: (c.price_change_percentage_24h||0).toFixed(2), positive: (c.price_change_percentage_24h||0) >= 0, mcap: c.market_cap, volume: c.total_volume, icon: c.image, id: c.id })));
        }).catch(() => setProjects([])).finally(() => setLoading(false));
    }, []);

    return (
        <div className="cv-app-content">
            <p className="cv-app-description">Discover trending tokens by volume. Data powered by CoinGecko.</p>
            {loading ? <div className="cv-market-loading"><div className="cv-market-spinner"></div><p>Loading token data...</p></div> : (
                <div className="cv-ido-list">
                    {projects.map((p, i) => (
                        <div key={i} className="cv-ido-card">
                            <div className="cv-ido-header">
                                {p.icon ? <img src={p.icon} alt={p.name} style={{width:'32px',height:'32px',borderRadius:'50%'}} /> : <span className="cv-ido-icon">🪙</span>}
                                <div className="cv-ido-title"><strong>{p.name}</strong><span className="cv-ido-symbol">{p.symbol}</span></div>
                                <span className={'cv-ido-status ' + (p.positive ? 'live' : 'ended')}>{p.positive ? '▲' : '▼'} {Math.abs(p.change)}%</span>
                            </div>
                            <div className="cv-ido-details">
                                <div className="cv-ido-detail"><span>Price</span><strong>{p.price}</strong></div>
                                {p.mcap   && <div className="cv-ido-detail"><span>Market Cap</span><strong>{formatLarge(p.mcap)}</strong></div>}
                                {p.volume && <div className="cv-ido-detail"><span>24h Volume</span><strong>{formatLarge(p.volume)}</strong></div>}
                            </div>
                            {p.id && <a href={'https://www.coingecko.com/en/coins/' + p.id} target="_blank" rel="noopener noreferrer" className="cv-app-btn" style={{display:'block',textAlign:'center',textDecoration:'none'}}>View Details ↗</a>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function YieldFarming() {
    const farms = [
        { pool: 'SOL/USDC', apy: '24.5%', tvl: '$12.4M', network: 'Solana',   protocol: 'Raydium',     url: 'https://raydium.io/liquidity/',         icon: '🌾' },
        { pool: 'ETH/USDT', apy: '18.2%', tvl: '$89.1M', network: 'Ethereum', protocol: 'Uniswap V3',  url: 'https://app.uniswap.org/#/pool',        icon: '🦄' },
        { pool: 'BNB/BUSD', apy: '31.7%', tvl: '$45.2M', network: 'BSC',      protocol: 'PancakeSwap', url: 'https://pancakeswap.finance/liquidity', icon: '🥞' },
        { pool: 'MATIC/ETH',apy: '15.8%', tvl: '$23.7M', network: 'Polygon',  protocol: 'QuickSwap',   url: 'https://quickswap.exchange/#/pool',     icon: '⚡' },
    ];
    return (
        <div className="cv-app-content">
            <p className="cv-app-description">Earn passive income by providing liquidity to DeFi protocols.</p>
            <div className="cv-farm-list">
                {farms.map((farm, i) => (
                    <div key={i} className="cv-farm-card">
                        <div className="cv-farm-header">
                            <span className="cv-farm-icon">{farm.icon}</span>
                            <div className="cv-farm-title"><strong>{farm.pool}</strong><span className="cv-farm-protocol">{farm.protocol}</span></div>
                            <div className="cv-farm-apy"><span className="cv-farm-apy-label">APY</span><strong className="cv-farm-apy-value">{farm.apy}</strong></div>
                        </div>
                        <div className="cv-farm-details">
                            <div className="cv-farm-detail"><span>TVL</span><strong>{farm.tvl}</strong></div>
                            <div className="cv-farm-detail"><span>Network</span><strong>{farm.network}</strong></div>
                        </div>
                        <button className="cv-app-btn" onClick={() => window.open(farm.url, '_blank', 'noopener,noreferrer')}>Add Liquidity ↗</button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function DAppBrowser() {
    const [url, setUrl] = useState('');
    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');
    const popularDApps = [
        { name: 'Uniswap',     url: 'https://app.uniswap.org',           icon: '🦄', desc: 'Swap tokens on Ethereum',  category: 'swap',  network: 'Ethereum' },
        { name: 'Jupiter',     url: 'https://jup.ag',                     icon: '🪐', desc: 'Best swap rates on Solana', category: 'swap',  network: 'Solana'   },
        { name: 'Raydium',     url: 'https://raydium.io/swap',            icon: '⚡', desc: 'Swap tokens on Solana',    category: 'swap',  network: 'Solana'   },
        { name: 'PancakeSwap', url: 'https://pancakeswap.finance/swap',   icon: '🥞', desc: 'Swap tokens on BSC',       category: 'swap',  network: 'BSC'      },
        { name: 'OpenSea',     url: 'https://opensea.io',                 icon: '🌊', desc: 'Largest NFT marketplace',  category: 'nft',   network: 'Ethereum' },
        { name: 'Magic Eden',  url: 'https://magiceden.io',               icon: '🪄', desc: 'Solana NFT marketplace',   category: 'nft',   network: 'Solana'   },
        { name: 'Blur',        url: 'https://blur.io',                    icon: '💨', desc: 'Pro NFT marketplace',      category: 'nft',   network: 'Ethereum' },
        { name: 'Aave',        url: 'https://app.aave.com',               icon: '👻', desc: 'Lend and borrow crypto',   category: 'defi',  network: 'Ethereum' },
        { name: 'Compound',    url: 'https://app.compound.finance',       icon: '🏦', desc: 'Earn interest on crypto',  category: 'defi',  network: 'Ethereum' },
        { name: 'Lido',        url: 'https://lido.fi',                    icon: '🔷', desc: 'Stake ETH and SOL',        category: 'defi',  network: 'Multi'    },

    ];
    const categories = [{id:'all',label:'All'},{id:'swap',label:'Swap'},{id:'nft',label:'NFTs'},{id:'defi',label:'DeFi'},{id:'other',label:'Other'}];
    const filteredDApps = popularDApps.filter(d => (activeCategory === 'all' || d.category === activeCategory) && (!search || d.name.toLowerCase().includes(search.toLowerCase()) || d.desc.toLowerCase().includes(search.toLowerCase())));
    const launchDApp = u => { window.open(u, '_blank', 'noopener,noreferrer'); };
    const handleCustomLaunch = () => { if (!url) return; launchDApp(url.startsWith('http') ? url : 'https://' + url); };
    return (
        <div className="cv-app-content">
            <p className="cv-app-description">Launch any DApp with your connected wallet. Opens securely in a new tab.</p>
            <div className="cv-dapp-search">
                <input type="text" className="cv-search-input" placeholder="Enter any DApp URL" value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCustomLaunch()} />
                <button className="cv-app-btn" onClick={handleCustomLaunch} disabled={!url}>Launch ↗</button>
            </div>
            <input type="text" className="cv-search-input" placeholder="Search DApps..." value={search} onChange={e => setSearch(e.target.value)} />
            <div className="cv-dapp-categories">
                {categories.map(cat => <button key={cat.id} className={'cv-dapp-category-btn ' + (activeCategory === cat.id ? 'active' : '')} onClick={() => setActiveCategory(cat.id)}>{cat.label}</button>)}
            </div>
            <div className="cv-dapp-grid">
                {filteredDApps.map((dapp, i) => (
                    <button key={i} className="cv-dapp-card" onClick={() => launchDApp(dapp.url)}>
                        <span className="cv-dapp-icon">{dapp.icon}</span>
                        <strong className="cv-dapp-name">{dapp.name}</strong>
                        <span className="cv-dapp-desc">{dapp.desc}</span>
                        <span className="cv-dapp-network-badge">{dapp.network}</span>
                        <span className="cv-dapp-launch-hint">Click to launch ↗</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

function PriceAlerts() {
    const { prices } = useWallet();
    const [alerts, setAlerts] = useState(() => {
        try { return JSON.parse(localStorage.getItem('cv_price_alerts') || '[]'); } catch { return []; }
    });
    const [coin,      setCoin     ] = useState('bitcoin');
    const [condition, setCondition] = useState('above');
    const [target,    setTarget   ] = useState('');
    const [triggered, setTriggered] = useState([]);

    const COINS = [
        { id: 'bitcoin',  label: 'BTC', price: prices?.bitcoin?.usd      || 0 },
        { id: 'ethereum', label: 'ETH', price: prices?.ethereum?.usd     || 0 },
        { id: 'solana',   label: 'SOL', price: prices?.solana?.usd       || 0 },
        { id: 'binancecoin', label: 'BNB', price: prices?.binancecoin?.usd || 0 },
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now();
            alerts.forEach(alert => {
                const coin = COINS.find(c => c.id === alert.coin);
                if (!coin || alert.triggered) return;
                const hit = alert.condition === 'above'
                    ? coin.price >= parseFloat(alert.target)
                    : coin.price <= parseFloat(alert.target);
                if (hit) {
                    setTriggered(prev => [...prev, alert.id]);
                    const updated = alerts.map(a => a.id === alert.id ? {...a, triggered: true} : a);
                    setAlerts(updated);
                    localStorage.setItem('cv_price_alerts', JSON.stringify(updated));
                }
            });
        }, 10000);
        return () => clearInterval(interval);
    }, [alerts, prices]);

    const addAlert = () => {
        if (!target || parseFloat(target) <= 0) return;
        const newAlert = { id: Date.now(), coin, condition, target: parseFloat(target), triggered: false, createdAt: Date.now() };
        const updated = [...alerts, newAlert];
        setAlerts(updated);
        localStorage.setItem('cv_price_alerts', JSON.stringify(updated));
        setTarget('');
        import('react-hot-toast').then(({default: toast}) => toast.success('Alert set!'));
    };

    const removeAlert = (id) => {
        const updated = alerts.filter(a => a.id !== id);
        setAlerts(updated);
        localStorage.setItem('cv_price_alerts', JSON.stringify(updated));
    };

    return (
        <div className="cv-app-content">
            <p className="cv-app-description">Set price alerts for your favourite coins. Get notified when price targets are hit.</p>
            <div style={{background:'rgba(255,255,255,0.04)',borderRadius:'12px',padding:'16px',border:'1px solid rgba(255,255,255,0.08)'}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr auto',gap:'10px',alignItems:'end'}}>
                    <div>
                        <label style={{fontSize:'11px',color:'#888',display:'block',marginBottom:'6px',textTransform:'uppercase'}}>Coin</label>
                        <select value={coin} onChange={e=>setCoin(e.target.value)} className="cv-select" style={{width:'100%'}}>
                            {COINS.map(c => <option key={c.id} value={c.id}>{c.label} — ${c.price.toLocaleString()}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{fontSize:'11px',color:'#888',display:'block',marginBottom:'6px',textTransform:'uppercase'}}>Condition</label>
                        <select value={condition} onChange={e=>setCondition(e.target.value)} className="cv-select" style={{width:'100%'}}>
                            <option value="above">Price goes above</option>
                            <option value="below">Price goes below</option>
                        </select>
                    </div>
                    <div>
                        <label style={{fontSize:'11px',color:'#888',display:'block',marginBottom:'6px',textTransform:'uppercase'}}>Target Price (USD)</label>
                        <input type="number" className="cv-input" placeholder="e.g. 100000" value={target} onChange={e=>setTarget(e.target.value)} />
                    </div>
                    <button onClick={addAlert} className="cv-btn-primary" style={{padding:'10px 20px'}}>+ Add Alert</button>
                </div>
            </div>
            {alerts.length === 0 ? (
                <div className="cv-app-empty"><div className="cv-app-empty-icon">🔔</div><p>No alerts set</p><span>Add a price alert above to get started</span></div>
            ) : (
                <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                    {alerts.map(alert => {
                        const coinData = COINS.find(c => c.id === alert.coin);
                        return (
                            <div key={alert.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 16px',background:alert.triggered?'rgba(0,229,160,0.06)':'rgba(255,255,255,0.04)',borderRadius:'10px',border:'1px solid '+(alert.triggered?'rgba(0,229,160,0.2)':'rgba(255,255,255,0.08)')}}>
                                <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                                    <span style={{fontSize:'20px'}}>🔔</span>
                                    <div>
                                        <div style={{fontWeight:'600',fontSize:'14px',color:'#fff'}}>{coinData?.label} {alert.condition === 'above' ? '▲ above' : '▼ below'} ${parseFloat(alert.target).toLocaleString()}</div>
                                        <div style={{fontSize:'12px',color:'#888'}}>Current: ${coinData?.price.toLocaleString()}</div>
                                    </div>
                                </div>
                                <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                                    {alert.triggered && <span style={{fontSize:'11px',padding:'3px 10px',borderRadius:'20px',background:'rgba(0,229,160,0.1)',color:'#00e5a0',fontWeight:'700'}}>✅ Triggered</span>}
                                    <button onClick={()=>removeAlert(alert.id)} style={{background:'none',border:'none',color:'#666',cursor:'pointer',fontSize:'16px'}}>✕</button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function CryptoCalendar() {
    const [events,  setEvents  ] = useState([]);
    const [loading, setLoading ] = useState(true);

    useEffect(() => {
        fetch('https://api.coingecko.com/api/v3/events?upcoming_events_only=true&per_page=20')
            .then(r => r.json())
            .then(data => {
                if (data.data) setEvents(data.data);
                else setEvents([]);
            })
            .catch(() => setEvents([]))
            .finally(() => setLoading(false));
    }, []);

    const STATIC_EVENTS = [
        { title: 'Bitcoin Halving Countdown', date: '2028-03-01', category: 'Cryptocurrency', description: 'Next Bitcoin halving expected around 2028', coins: ['BTC'] },
        { title: 'Ethereum Devcon 2025',       date: '2025-11-01', category: 'Conference',     description: 'Annual Ethereum developer conference',  coins: ['ETH'] },
        { title: 'Solana Breakpoint 2025',     date: '2025-09-01', category: 'Conference',     description: 'Annual Solana ecosystem conference',    coins: ['SOL'] },
    ];

    const allEvents = events.length > 0 ? events : STATIC_EVENTS;

    const getCategoryColor = (cat) => {
        if (!cat) return '#7C6FFF';
        const c = cat.toLowerCase();
        if (c.includes('conference')) return '#0ea5e9';
        if (c.includes('launch'))    return '#00e5a0';
        if (c.includes('halving'))   return '#F7931A';
        if (c.includes('listing'))   return '#FC72FF';
        return '#7C6FFF';
    };

    return (
        <div className="cv-app-content">
            <p className="cv-app-description">Upcoming crypto events, conferences, token launches and important dates.</p>
            {loading ? (
                <div className="cv-market-loading"><div className="cv-market-spinner"></div><p>Loading events...</p></div>
            ) : (
                <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                    {allEvents.map((ev, i) => {
                        const date     = new Date(ev.date || ev.start_date);
                        const isPast   = date < new Date();
                        const daysLeft = Math.ceil((date - new Date()) / (1000*60*60*24));
                        return (
                            <div key={i} style={{display:'flex',gap:'14px',padding:'14px 16px',background:'rgba(255,255,255,0.04)',borderRadius:'12px',border:'1px solid rgba(255,255,255,0.08)',opacity:isPast?0.5:1}}>
                                <div style={{textAlign:'center',minWidth:'50px',flexShrink:0}}>
                                    <div style={{fontSize:'20px',fontWeight:'800',color:'#fff'}}>{date.getDate()}</div>
                                    <div style={{fontSize:'11px',color:'#888'}}>{date.toLocaleString('default',{month:'short'})}</div>
                                    <div style={{fontSize:'10px',color:'#666'}}>{date.getFullYear()}</div>
                                </div>
                                <div style={{flex:1}}>
                                    <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'4px',flexWrap:'wrap'}}>
                                        <span style={{fontWeight:'700',fontSize:'14px',color:'#fff'}}>{ev.title}</span>
                                        <span style={{fontSize:'10px',padding:'2px 8px',borderRadius:'20px',background:getCategoryColor(ev.category)+'20',color:getCategoryColor(ev.category),fontWeight:'600'}}>{ev.category || 'Event'}</span>
                                    </div>
                                    <div style={{fontSize:'12px',color:'#aaa',lineHeight:'1.5'}}>{ev.description || ''}</div>
                                    {!isPast && daysLeft > 0 && <div style={{fontSize:'11px',color:'#FC72FF',marginTop:'4px',fontWeight:'600'}}>{daysLeft} days away</div>}
                                    {isPast && <div style={{fontSize:'11px',color:'#666',marginTop:'4px'}}>Completed</div>}
                                </div>
                                {ev.website && (
                                    <a href={ev.website} target="_blank" rel="noopener noreferrer" style={{alignSelf:'center',fontSize:'12px',color:'#FC72FF',textDecoration:'none',flexShrink:0}}>↗</a>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function GasTracker() {
    const [gasData, setGasData] = useState(null);
    const [loading, setLoading ] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchGas = async () => {
        setLoading(true);
        try {
            const res  = await fetch('https://api.etherscan.io/api?module=gastracker&action=gasoracle');
            const data = await res.json();
            if (data.result) {
                setGasData(data.result);
                setLastUpdated(new Date().toLocaleTimeString());
            }
        } catch (err) {
            setGasData({ SafeGasPrice: '15', ProposeGasPrice: '18', FastGasPrice: '25' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchGas(); const t = setInterval(fetchGas, 30000); return () => clearInterval(t); }, []);

    const GAS_SPEEDS = [
        { label: 'Standard',  key: 'SafeGasPrice',    icon: '🐢', color: '#00e5a0', time: '~5 min'  },
        { label: 'Fast',      key: 'ProposeGasPrice',  icon: '🚗', color: '#ffaa33', time: '~2 min'  },
        { label: 'Rapid',     key: 'FastGasPrice',     icon: '🚀', color: '#FC72FF', time: '< 30 sec'},
    ];

    return (
        <div className="cv-app-content">
            <p className="cv-app-description">Live Ethereum gas fees. Updated every 30 seconds.</p>
            {loading && !gasData ? (
                <div className="cv-market-loading"><div className="cv-market-spinner"></div><p>Fetching gas prices...</p></div>
            ) : (
                <>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'12px',marginBottom:'16px'}}>
                        {GAS_SPEEDS.map(speed => (
                            <div key={speed.key} style={{background:'rgba(255,255,255,0.04)',borderRadius:'14px',padding:'20px',textAlign:'center',border:'1px solid rgba(255,255,255,0.08)'}}>
                                <div style={{fontSize:'28px',marginBottom:'8px'}}>{speed.icon}</div>
                                <div style={{fontSize:'11px',color:'#888',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'6px'}}>{speed.label}</div>
                                <div style={{fontSize:'28px',fontWeight:'800',color:speed.color,marginBottom:'4px'}}>{gasData?.[speed.key] || '—'}</div>
                                <div style={{fontSize:'11px',color:'#888'}}>Gwei</div>
                                <div style={{fontSize:'11px',color:'#666',marginTop:'4px'}}>{speed.time}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',marginBottom:'12px'}}>
                        {[
                            { label: 'ETH Transfer',      gas: 21000  },
                            { label: 'ERC20 Transfer',    gas: 65000  },
                            { label: 'Uniswap Swap',      gas: 150000 },
                            { label: 'NFT Mint',          gas: 200000 },
                        ].map((item, i) => {
                            const gwei    = parseFloat(gasData?.ProposeGasPrice || 18);
                            const ethCost = (item.gas * gwei * 1e-9).toFixed(6);
                            return (
                                <div key={i} style={{background:'rgba(255,255,255,0.04)',borderRadius:'10px',padding:'12px',border:'1px solid rgba(255,255,255,0.06)'}}>
                                    <div style={{fontSize:'12px',color:'#888',marginBottom:'4px'}}>{item.label}</div>
                                    <div style={{fontWeight:'700',color:'#fff',fontSize:'14px'}}>{ethCost} ETH</div>
                                </div>
                            );
                        })}
                    </div>
                    {lastUpdated && <div style={{fontSize:'11px',color:'#666',textAlign:'center'}}>Last updated: {lastUpdated} · Auto-refreshes every 30s</div>}
                    <button onClick={fetchGas} className="cv-btn-secondary" style={{width:'100%',marginTop:'10px'}}>⟳ Refresh Now</button>
                </>
            )}
        </div>
    );
}

function TopHolders() {
    const [coins,       setCoins      ] = useState([]);
    const [loading,     setLoading    ] = useState(true);
    const [activeCoin,  setActiveCoin ] = useState('bitcoin');
    const [holders,     setHolders    ] = useState([]);
    const [loadingH,    setLoadingH   ] = useState(false);

    const TOP_COINS = [
        { id: 'bitcoin',     label: 'BTC', color: '#F7931A' },
        { id: 'ethereum',    label: 'ETH', color: '#627EEA' },
        { id: 'solana',      label: 'SOL', color: '#9945FF' },
        { id: 'binancecoin', label: 'BNB', color: '#F0B90B' },
    ];

    // Simulated whale wallets data since on-chain holder API requires paid access
    const WHALE_DATA = {
        bitcoin: [
            { address: '1A1zP1eP5QGefi2DMPTfTL5SLmv7Divf…', balance: '1,000,000 BTC', label: 'Satoshi Genesis', type: 'Genesis' },
            { address: '3M219KR5vEneNb47ewrPfWyb5jQ2DjxRP…', balance: '248,597 BTC',   label: 'Unknown Whale',  type: 'Whale'   },
            { address: 'bc1qgdjqv0av3q56jvd82tkdjpy7gdp9…', balance: '125,000 BTC',   label: 'Exchange Cold',  type: 'Exchange'},
            { address: '1FeexV6bAHb8ybZjqQMjJrcCrHGW9sb6…', balance: '79,957 BTC',    label: 'Unknown Whale',  type: 'Whale'   },
            { address: '34xp4vRoCGJym3xR7yCVPFHoCNxv4Twseo', balance: '248,597 BTC',  label: 'Binance Cold',   type: 'Exchange'},
        ],
        ethereum: [
            { address: '0xC02aaA39b223FE8D0A0e5C4F27eAD908…', balance: '3,024,953 ETH', label: 'WETH Contract',  type: 'Contract' },
            { address: '0xBE0eB53F46cd790Cd13851d5EFf43D12…', balance: '1,996,008 ETH', label: 'Binance 7',      type: 'Exchange' },
            { address: '0x00000000219ab540356cBB839Cbe05303…', balance: '985,986 ETH',  label: 'ETH2 Deposit',   type: 'Contract' },
            { address: '0x8315177aB297bA92A06054cE80a67Ed4…', balance: '610,000 ETH',   label: 'Arbitrum Bridge', type: 'Bridge'  },
            { address: '0x40B38765696e3d5d8d9d834D8AaD4bB6…', balance: '358,914 ETH',   label: 'Robinhood',      type: 'Exchange' },
        ],
        solana: [
            { address: '9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM', balance: '27,000,000 SOL', label: 'FTX Estate',    type: 'Exchange' },
            { address: 'GThUX1Atko4tqhN2NaiTazFAcaPNtRZQd5drCbULyGxX', balance: '11,000,000 SOL', label: 'Alameda',      type: 'Whale'    },
            { address: 'CakcnaRDHka2gXyfxNwCsWGFVjr9GqUfydrmwEBPCsH',  balance: '8,200,000 SOL',  label: 'Unknown Whale', type: 'Whale'   },
            { address: 'BVQK7GBBVUUPTFzAB5PdtcEMRRsZFrYv3HnLSiGXUzk',  balance: '5,800,000 SOL',  label: 'Jump Crypto',  type: 'Fund'    },
            { address: '5tzFkiKscXHK5ynDjFV7WZR9ERqhefgKpZ5xaGBbEDH',  balance: '4,200,000 SOL',  label: 'Binance Cold', type: 'Exchange'},
        ],
        binancecoin: [
            { address: '0xF977814e90dA44bFA03b6295A0616a897441aceC', balance: '5,795,000 BNB', label: 'Binance Hot 8',  type: 'Exchange' },
            { address: '0xBE0eB53F46cd790Cd13851d5EFf43D12404d33E8', balance: '3,900,000 BNB', label: 'Binance Cold',   type: 'Exchange' },
            { address: '0x8894E0a0c962CB723c1976a4421c95949bE2D4E3', balance: '1,200,000 BNB', label: 'Unknown Whale',  type: 'Whale'    },
            { address: '0x3c783C21A0383057D128bae431894a5C19F9Cf06', balance: '980,000 BNB',   label: 'Unknown Whale',  type: 'Whale'    },
            { address: '0x5a52E96BAcdaBb82fd05763E25335261B270Efcb', balance: '850,000 BNB',   label: 'Binance Pool',   type: 'Exchange' },
        ],
    };

    const getTypeColor = (type) => {
        if (type === 'Exchange') return '#F7931A';
        if (type === 'Whale')    return '#FC72FF';
        if (type === 'Contract') return '#627EEA';
        if (type === 'Bridge')   return '#00e5a0';
        if (type === 'Fund')     return '#9945FF';
        return '#888';
    };

    return (
        <div className="cv-app-content">
            <p className="cv-app-description">Track the largest crypto wallets and whale movements on the blockchain.</p>
            <div style={{display:'flex',gap:'8px',marginBottom:'16px',flexWrap:'wrap'}}>
                {TOP_COINS.map(c => (
                    <button key={c.id} onClick={()=>setActiveCoin(c.id)} style={{padding:'7px 16px',borderRadius:'20px',border:'none',cursor:'pointer',fontFamily:'inherit',fontWeight:'600',fontSize:'13px',background:activeCoin===c.id?c.color:'rgba(255,255,255,0.06)',color:activeCoin===c.id?'#fff':'#aaa'}}>
                        {c.label}
                    </button>
                ))}
            </div>
            <div style={{background:'rgba(255,255,255,0.02)',borderRadius:'12px',border:'1px solid rgba(255,255,255,0.07)',overflow:'hidden'}}>
                <div style={{display:'grid',gridTemplateColumns:'auto 1fr 1fr auto',gap:'0',padding:'10px 16px',background:'#0d0d0d',borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                    {['#','Address','Balance','Type'].map((h,i) => (
                        <div key={i} style={{fontSize:'11px',fontWeight:'700',color:'#666',textTransform:'uppercase',letterSpacing:'0.8px'}}>{h}</div>
                    ))}
                </div>
                {(WHALE_DATA[activeCoin] || []).map((holder, i) => (
                    <div key={i} style={{display:'grid',gridTemplateColumns:'auto 1fr 1fr auto',gap:'0',padding:'14px 16px',borderBottom:'1px solid rgba(255,255,255,0.04)',alignItems:'center',background:i%2===0?'#111':'#0d0d0d'}}>
                        <div style={{fontSize:'13px',color:'#666',minWidth:'28px'}}>#{i+1}</div>
                        <div>
                            <div style={{fontSize:'13px',fontWeight:'600',color:'#fff',marginBottom:'2px'}}>{holder.label}</div>
                            <div style={{fontSize:'11px',color:'#666',fontFamily:'monospace'}}>{holder.address}</div>
                        </div>
                        <div style={{fontSize:'13px',fontWeight:'700',color:'#fff'}}>{holder.balance}</div>
                        <div>
                            <span style={{fontSize:'11px',padding:'3px 10px',borderRadius:'20px',background:getTypeColor(holder.type)+'18',color:getTypeColor(holder.type),fontWeight:'700',border:'1px solid '+getTypeColor(holder.type)+'33'}}>{holder.type}</span>
                        </div>
                    </div>
                ))}
            </div>
            <p style={{fontSize:'11px',color:'#555',textAlign:'center',marginTop:'10px'}}>Data is approximate and for informational purposes only.</p>
        </div>
    );
}


function AppsPage() {
    const [activeApp, setActiveApp] = useState('portfolio');
    const apps = [
        { id: 'portfolio',  label: 'Portfolio Tracker',  icon: '📊' },
        { id: 'nft',        label: 'NFT Gallery',        icon: '🖼️' },
        { id: 'farming',    label: 'DeFi Farming',        icon: '🌾' },
        { id: 'dapp',       label: 'DApp Browser',       icon: '🌐' },
        { id: 'alerts',   label: 'Price Alerts',    icon: '🔔' },
        { id: 'calendar', label: 'Crypto Calendar',  icon: '📅' },
        { id: 'gas',      label: 'Gas Tracker',     icon: '⛽' },
        { id: 'whales',   label: 'Top Holders',     icon: '🏆' },
    ];
    const renderApp = () => {
        switch (activeApp) {
            case 'portfolio':  return <PortfolioTracker />;
            case 'nft':        return <NFTGallery />;
            case 'farming':    return <YieldFarming />;
            case 'dapp':       return <DAppBrowser />;
            case 'alerts':   return <PriceAlerts />;
            case 'calendar': return <CryptoCalendar />;
            case 'gas':      return <GasTracker />;
            case 'whales':   return <TopHolders />;
            default:           return <PortfolioTracker />;
        }
    };
    return (
        <div className="cv-page cv-apps-page">
            <div className="cv-apps-sidebar">
                <h2 className="cv-apps-sidebar-title">Wallet Apps</h2>
                {apps.map(app => (
                    <button key={app.id} className={'cv-app-tab ' + (activeApp === app.id ? 'active' : '')} onClick={() => setActiveApp(app.id)}>
                        <span className="cv-app-tab-icon">{app.icon}</span>
                        <span className="cv-app-tab-label">{app.label}</span>
                    </button>
                ))}
            </div>
            <div className="cv-apps-main">
                <div className="cv-apps-header">
                    <h2 className="cv-apps-title">{apps.find(a => a.id === activeApp)?.icon} {apps.find(a => a.id === activeApp)?.label}</h2>
                </div>
                {renderApp()}
            </div>
        </div>
    );
}

export default AppsPage;
